// lineRating.js
//
// Browser-side arithmetic for the line rating panel (#247). The rating itself is
// computed by the API (DataIngestionESM/lib/dlr.mjs); what lives here is only
// what has to stay in the browser: the operator's present load. It is typed,
// turned into a current, and read against the curve the API sent, and it never
// leaves this tab.

/** Parse a typed number, accepting a decimal comma. Empty or invalid gives null. */
export function parseNumber(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim().replace(',', '.');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Line current from three-phase power: I = S / (sqrt(3) * V_LL), with
 * S = sqrt(P^2 + Q^2). The phase angle is not needed; it is already in P and Q.
 *
 * @param {number} pMw     active power, MW
 * @param {number} qMvar   reactive power, Mvar (sign does not matter)
 * @param {number} vKv     line-to-line voltage, kV
 * @returns {number|null}  amperes
 */
export function currentFromPower(pMw, qMvar, vKv) {
  if (![pMw, qMvar, vKv].every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
  if (vKv <= 0) return null;
  const sMva = Math.hypot(pMw, qMvar);
  return (sMva * 1000) / (Math.sqrt(3) * vKv);
}

/**
 * The typed load against each of the three ratings (#260).
 *
 * Static, AAR and DLR answer three different questions and an operator needs
 * all three to read the fourth: how much of the headroom we are showing them
 * was already in their own static rating, and how much came from measuring the
 * wind at the span. Quoting DLR against static alone claims both as ours.
 *
 * A rating of 0 A is a real answer, not a missing one: the sun alone can hold
 * the conductor over Tmax in dead calm. There is no utilisation to report
 * against it, so `utilisation` is null rather than Infinity, while `headroom`
 * stays a number because being 400 A over a 0 A rating is exactly the thing
 * worth saying.
 *
 * @param {{static_rating_a:number, aar_a?:number, ampacity_a:number}} rating
 * @param {number} currentA
 * @returns {Array<{key:string, rating:number, utilisation:number|null, headroom:number}>}
 */
export function loadAgainstRatings(rating, currentA) {
  if (!rating || typeof currentA !== 'number' || !Number.isFinite(currentA)) return [];
  return [
    ['static', rating.static_rating_a],
    ['aar', rating.aar_a],
    ['dlr', rating.ampacity_a],
  ]
    // AAR is absent from a response served before #260. Drop the row rather
    // than drawing a dash: a blank column invites the reader to assume zero.
    .filter(([, a]) => typeof a === 'number' && Number.isFinite(a))
    .map(([key, a]) => ({
      key,
      rating: a,
      utilisation: a > 0 ? (currentA / a) * 100 : null,
      headroom: a - currentA,
    }));
}

/**
 * Amperes of headroom the measured weather adds over the line's static rating,
 * or null when either figure is missing. Negative is a real answer and is the
 * one that matters most: on a hot still day the span is worth less than the
 * static rating assumes, and the operator has to be told so plainly.
 */
export function headroomGainOverStatic(rating) {
  if (!rating) return null;
  const { ampacity_a: dlr, static_rating_a: stat } = rating;
  if (![dlr, stat].every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
  return dlr - stat;
}

/**
 * Conductor temperature for `currentA`, interpolated on the API's curve of
 * { t_c, current_a } points (ascending). Outside the curve the answer is a bound,
 * not an extrapolation: `below` means cooler than the first point, `above`
 * hotter than the last.
 *
 * @returns {{ tC: number, bound: 'below'|'above'|null } | null}
 */
export function temperatureForCurrent(curve, currentA) {
  if (!Array.isArray(curve) || curve.length < 2) return null;
  if (typeof currentA !== 'number' || !Number.isFinite(currentA) || currentA < 0) return null;
  const first = curve[0];
  const last = curve[curve.length - 1];
  if (currentA <= first.current_a) return { tC: first.t_c, bound: 'below' };
  if (currentA > last.current_a) return { tC: last.t_c, bound: 'above' };
  for (let i = 1; i < curve.length; i += 1) {
    const a = curve[i - 1];
    const b = curve[i];
    if (currentA <= b.current_a) {
      const span = b.current_a - a.current_a;
      const f = span > 0 ? (currentA - a.current_a) / span : 0;
      return { tC: a.t_c + f * (b.t_c - a.t_c), bound: null };
    }
  }
  return { tC: last.t_c, bound: 'above' };
}
